<template>
  <v-main>
    <v-container fluid>
      <v-row justify="center" class="mt-4">
        <v-col>
          <span class="text-h5 font-weight-bold" v-text="'Certificates'"></span>
          <!-- Offline/Online indicator -->
          <v-tooltip bottom v-if="isOffline">
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" class="ml-2" color="orange">
                mdi-wifi-off
              </v-icon>
            </template>
            <span>Offline Mode - Showing cached data</span>
          </v-tooltip>
          <v-tooltip bottom v-else>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" class="ml-2" color="green">
                mdi-wifi
              </v-icon>
            </template>
            <span>Online Mode</span>
          </v-tooltip>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6">
            <v-card-text>
              <v-row align="center">
                <v-col md="5">
                  <v-text-field
                    v-model="search_keyword"
                    label="Search candidate by name or email ..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col md="4">
                  <v-pagination
                    v-model="page"
                    circle
                    :length="paginationLength"
                  >
                  </v-pagination>
                </v-col>
                <v-col md="3" class="text-right">
                  <v-chip small color="info" outlined>
                    {{ filteredCandidates.length }} candidate(s)
                  </v-chip>
                  <v-chip small color="success" outlined class="ml-1">
                    {{ allCertificates.length }} certificate(s)
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
      ></v-progress-linear>

      <v-row v-else>
        <v-col md="12">
          <v-card elevation="6">
            <!-- Column headers -->
            <v-row
              v-if="paginatedCandidates.length"
              no-gutters
              class="px-4 py-2 grey--text text--darken-1 font-weight-bold"
              style="border-bottom: 1px solid #e0e0e0"
            >
              <v-col cols="1">Sr No</v-col>
              <v-col cols="4">Name</v-col>
              <v-col cols="5">Email</v-col>
              <v-col cols="2" class="text-center">Certificates</v-col>
            </v-row>

            <!-- One expandable panel per candidate -->
            <v-expansion-panels accordion flat>
              <v-expansion-panel
                v-for="(cand, i) in paginatedCandidates"
                :key="`cand-${cand.id}`"
              >
                <v-expansion-panel-header>
                  <v-row no-gutters align="center">
                    <v-col cols="1">
                      {{ (page - 1) * rowsPerPage + i + 1 }}
                    </v-col>
                    <v-col cols="4" class="font-weight-medium">
                      {{ cand.name }} {{ cand.last_name }}
                    </v-col>
                    <v-col cols="5">{{ cand.email }}</v-col>
                    <v-col cols="2" class="text-center">
                      <v-chip small color="primary" dark>
                        {{ cand.certificates.length }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Course</th>
                          <th class="text-left">Subject</th>
                          <th class="text-left">Certificate No.</th>
                          <th class="text-left">Certificate</th>
                          <th class="text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="cert in cand.certificates"
                          :key="`cert-${cert.id}`"
                        >
                          <td>{{ cert.course ? cert.course.name : "" }}</td>
                          <td>
                            {{
                              cert.course && cert.course.subject
                                ? cert.course.subject.description
                                : ""
                            }}
                          </td>
                          <td>{{ cert.cert_no || "" }}</td>
                          <td>
                            <v-btn
                              small
                              color="success"
                              @click="downloadCertificate(cert)"
                              :loading="downloadingCertId === cert.id"
                              :disabled="downloadingCertId === cert.id"
                            >
                              <v-icon small left>mdi-download</v-icon>
                              Download
                            </v-btn>
                          </td>
                          <td>
                            <v-btn
                              color="error"
                              fab
                              dark
                              x-small
                              @click="Delete_this(cert.id)"
                            >
                              <v-icon size="16">mdi-delete</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <div
              v-if="!paginatedCandidates.length"
              class="text-center pa-6 grey--text"
            >
              No certificates found
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import {
  generateCertificatePDF,
  formatCertificateDate,
} from "@/services/certificatePdfService";

export default {
  name: "CertificatePage",
  data() {
    return {
      page: 1,
      search_keyword: "",
      allCertificates: [], // every completed enrollment (one per course)
      isLoading: false,
      isOffline: false,
      downloadingCertId: null,
    };
  },
  computed: {
    // Group certificates by candidate -> [{ id, name, email, certificates: [] }]
    groupedCandidates() {
      const map = new Map();
      for (const cert of this.allCertificates) {
        const cand = cert.candidate || {};
        const key = cand.id || cert.candidate_id || "unknown";
        if (!map.has(key)) {
          map.set(key, {
            id: key,
            name: cand.name || "",
            last_name: cand.last_name || "",
            email: cand.email || "",
            certificates: [],
          });
        }
        map.get(key).certificates.push(cert);
      }
      return Array.from(map.values());
    },
    // Client-side search by candidate name / email
    filteredCandidates() {
      const q = (this.search_keyword || "").toLowerCase().trim();
      if (!q) return this.groupedCandidates;
      return this.groupedCandidates.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.last_name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q)
      );
    },
    paginatedCandidates() {
      const start = (this.page - 1) * this.rowsPerPage;
      return this.filteredCandidates.slice(start, start + this.rowsPerPage);
    },
    paginationLength() {
      return Math.ceil(this.filteredCandidates.length / this.rowsPerPage) || 1;
    },
  },
  watch: {
    // Reset to first page whenever the search changes
    search_keyword() {
      this.page = 1;
    },
  },
  async mounted() {
    this.checkOfflineStatus();
    await this.getData();
  },
  methods: {
    checkOfflineStatus() {
      this.isOffline = window.electron && !navigator.onLine;
    },

    async getData() {
      this.isLoading = true;
      try {
        // Fetch all completed enrollments once; grouping/paging is done client-side.
        const response = await axios.get(
          `course_candidates?is_completed=true&rowsPerPage=100000&page=1`
        );
        this.allCertificates = (response.data && response.data.data) || [];
      } catch (error) {
        console.error("Error loading certificate data:", error);
        if (this.$toast) this.$toast.error("Failed to load certificate data");
        this.allCertificates = [];
      }
      this.isLoading = false;
    },

    async Delete_this(id) {
      let check = confirm("Are you sure you want to delete this certificate?");
      if (!check) return;
      try {
        this.isLoading = true;
        await axios.delete(`course_candidates/${id}`);
        await this.getData();
      } catch (e) {
        console.error("Error deleting certificate:", e);
        this.isLoading = false;
      }
    },

    // Build certificate data from a course candidate record
    buildCertificateData(certificate) {
      const candidate = certificate.candidate || {};
      const course = certificate.course || {};
      return {
        candidateName: `${candidate.name || ""} ${
          candidate.last_name || ""
        }`.trim(),
        cdcNumber: candidate.cdc || "N/A",
        courseName: course.name || "",
        completionDate: formatCertificateDate(
          certificate.updated_at || certificate.created_at
        ),
        presentedDate: formatCertificateDate(
          certificate.updated_at || certificate.created_at
        ),
        validityPeriod: course.validity_period || "12 months",
        certNo: certificate.cert_no || "",
      };
    },

    async downloadCertificate(certificate) {
      if (this.downloadingCertId) return;
      this.downloadingCertId = certificate.id;
      try {
        const certificateData = this.buildCertificateData(certificate);
        await generateCertificatePDF(certificateData);
        if (this.$toast)
          this.$toast.success("Certificate downloaded successfully");
      } catch (error) {
        console.error("Error downloading certificate:", error);
        if (this.$toast)
          this.$toast.error("Failed to download certificate. Please try again.");
      } finally {
        this.downloadingCertId = null;
      }
    },
  },
};
</script>
